import React from 'react';
import { Typography } from '@pwskills/rachnaui';
import {
  Youtube,
  PeopleOutline,
  BriefcaseSharp,
  Linkedin,
  Facebook,
  Instagram,
  Twitter,
} from '@pwskills/rachnaui/Icons';
import Image from 'next/image';
import Link from 'next/link';
import { IInstructorDetails, ISocialLinks } from '@/interfaces/courseDescription';
import { useRouter } from 'next/router';

interface Props {
  instructorDetails: IInstructorDetails;
  name: string;
  description: string;
  social: ISocialLinks;
  isLinkedInButtonEnabled: boolean;
  isSocialIconEnabled: boolean;
  learnersPlacedToBeShown: boolean;
  industryExpToBeShown: boolean;
}

const getSocialUrl = (value?: string | { url?: string }) =>
  typeof value === 'string' ? value : value?.url;

const CardDetailsView = (props: Props) => {
  const {
    instructorDetails,
    name,
    description,
    social,
    isLinkedInButtonEnabled,
    isSocialIconEnabled,
    learnersPlacedToBeShown,
    industryExpToBeShown,
  } = props;
  const {
    companies,
    designation,
    workExperience,
    teachingExperience,
    industryExp,
    learnersPlaced,
  } = instructorDetails || {};
  const router = useRouter();
  const isDashboard = router.pathname.includes('/dashboard');
  return (
    <div className="p-5">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <div className="flex flex-row justify-between items-center">
            <div>
              <Typography
                component={isDashboard ? 'body-regular' : 'h4'}
                variant={isDashboard ? 'semi-bold' : 'bold'}
                className="break-words text-gray-900"
              >
                {name}
              </Typography>
              <Typography
                component={isDashboard ? 'body-tiny' : 'body-small'}
                variant="regular"
                className="text-gray-650 mb-2"
              >
                {designation}
              </Typography>
              {isSocialIconEnabled && (
                <div className="flex gap-2">
                  {Object.entries({
                    linkedin: {
                      url: getSocialUrl(social?.linkedin),
                      Component: Linkedin,
                      hoverColor: 'hover:text-[#0177B5]',
                    },
                    youtube: {
                      url: getSocialUrl(social?.youtube),
                      Component: Youtube,
                      hoverColor: 'hover:text-[#FF0002]',
                    },
                    facebook: {
                      url: getSocialUrl(social?.facebook),
                      Component: Facebook,
                      hoverColor: 'hover:text-[#4867AA]',
                    },
                    instagram: {
                      url: getSocialUrl(social?.instagram),
                      Component: Instagram,
                      hoverColor: 'hover:text-[#B806E4]',
                    },
                    twitter: {
                      url: getSocialUrl(social?.twitter),
                      Component: Twitter,
                      hoverColor: 'hover:text-[#1DA1F1]',
                    },
                  }).map(
                    ([key, { url, Component, hoverColor }]) =>
                      url && (
                        <a key={key} href={url} target="_blank" rel="noreferrer noopener">
                          <Component className={`text-gray-600 w-5 h-5 ${hoverColor}`} />
                        </a>
                      )
                  )}
                </div>
              )}
            </div>

            {isLinkedInButtonEnabled && getSocialUrl(social?.linkedin) && (
              <div>
                <Link href={getSocialUrl(social?.linkedin) || ''} target="_blank">
                  <Image
                    src="/images/categoryPage/linkedin.svg"
                    height={25}
                    width={25}
                    alt="linkedin"
                  />
                </Link>
              </div>
            )}
          </div>

          {companies && (
            <Typography component="body-small" className="text-gray-850 ">
              {companies?.map(({ company }: { company: any }) => company).join(' | ')}
            </Typography>
          )}
        </div>
        <div className="flex gap-3">
          <div className="flex  items-center gap-2">
            <BriefcaseSharp className="w-7 h-7 text-gray-900" />
            {industryExp && industryExpToBeShown ? (
              <div>
                <Typography component="body-small" variant="bold" className="text-gray-900">
                  {industryExp}+ Years
                </Typography>
                <Typography component="body-tiny" variant="regular" className="text-gray-650 ">
                  Industry Experience
                </Typography>
              </div>
            ) : (
              <div>
                <Typography component="body-small" variant="bold" className="text-gray-900">
                  {workExperience}+ Years
                </Typography>
                <Typography component="body-tiny" variant="regular" className="text-gray-650 ">
                  Work Experience
                </Typography>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <PeopleOutline className="w-7 h-7 text-gray-900" />
            {learnersPlaced && learnersPlacedToBeShown ? (
              <div>
                <Typography component="body-small" variant="bold" className="text-gray-900">
                  {learnersPlaced}+
                </Typography>
                <Typography component="body-tiny" variant="regular" className="text-gray-650 ">
                  Learners Placed
                </Typography>
              </div>
            ) : (
              <div>
                <Typography component="body-small" variant="bold" className="text-gray-900">
                  {teachingExperience}+ Years
                </Typography>
                <Typography component="body-tiny" variant="regular" className="text-gray-650 ">
                  Teaching Experience
                </Typography>
              </div>
            )}
          </div>
        </div>
        <Typography
          component="body-small"
          variant="regular"
          className="line-clamp-3 text-[#757575]"
        >
          {description}
        </Typography>
      </div>
    </div>
  );
};

export default CardDetailsView;
